'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ActiveBonuses } from './sections/ActiveBonuses';
import { AllOffers } from './sections/AllOffers';
import { BonusHistory } from './sections/BonusHistory';
import { Sports } from './sections/Sports';
import { AnalyticsEventName } from '@/api/analytics';
import { CancelBonusModal } from '@/components/modals/CancelBonusModal';
import { TabNavigation } from '@/components/TabNavigation';
import { removeQueryParameter } from '@/helpers/common';
import { useAnalytics } from '@/hooks/analytics';
import useEstonia from '@/hooks/estonia';
import { useCommonStore } from '@/store';

import { ScPromotionsPage } from './styled';
import { ScContainer } from '@/styles/common';

export enum PromotionPageTabs {
  ACTIVE_BONUSES = 'active-bonuses',
  BONUS_HISTORY = 'bonus-history',
  SPORTS = 'sports',
  CASINO = 'casino',
}

export interface Tab {
  title: string;
  component: JSX.Element;
  id: PromotionPageTabs;
  isHidden?: boolean;
}

export const PromotionsPage = () => {
  const { t } = useTranslation('bonuses');
  const { sendEventToAnalytics } = useAnalytics();
  const { isEstonia } = useEstonia();

  const {
    isMobileDevice,
    isAuthenticated,
    pendingUser,
    fetchBonuses,
    currentCancelBonusData,
  } = useCommonStore(
    useShallow((state) => ({
      isMobileDevice: state.isMobileDevice,
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      fetchBonuses: state.fetchBonuses,
      currentCancelBonusData: state.currentCancelBonusData,
    }))
  );

  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const searchParams = useSearchParams();

  const { push } = useRouter();

  const tabs: Tab[] = useMemo(
    () =>
      [
        {
          title: t('tabs.casino'),
          component: (
            <AllOffers
              onPromocodeActivationSuccess={() => setCurrentTabIndex(2)}
            />
          ),
          id: PromotionPageTabs.CASINO,
        },
        {
          title: t('tabs.sports'),
          component: <Sports />,
          id: PromotionPageTabs.SPORTS,
          isHidden: isEstonia,
        },
        {
          title: t('tabs.myBonuses'),
          component: <ActiveBonuses />,
          id: PromotionPageTabs.ACTIVE_BONUSES,
        },
        {
          title: t('tabs.bonusHistory'),
          component: <BonusHistory />,
          id: PromotionPageTabs.BONUS_HISTORY,
        },
      ].filter((tab) => !tab.isHidden),
    [t, isEstonia]
  );

  const currentTabComponent = useMemo(
    () => tabs[currentTabIndex].component,
    [currentTabIndex, tabs]
  );

  const tabNavigationProps = useMemo(
    () =>
      isMobileDevice
        ? {
            fullScreen: true,
          }
        : {
            maxWidth100: true,
            withoutShadow: true,
            menuItemWidth: `${100 / tabs.length}%`,
          },
    [isMobileDevice, tabs.length]
  );

  useEffect(() => {
    const tab = searchParams.get('tab');
    const tabIndex = tabs.findIndex(({ id }) => id.toString() === tab);

    if (tabIndex >= 0) {
      setCurrentTabIndex(tabIndex);
    }
  }, [searchParams, tabs]);

  const handleSelectTab = useCallback(
    (index: number) => {
      setCurrentTabIndex(index);

      const tabId = tabs[index].id;

      removeQueryParameter(['tab', 'block'], push);

      sendEventToAnalytics({
        name: AnalyticsEventName.PROMO__TAB__SELECTION,
        attributes: {
          additionalData: JSON.stringify({ tabName: tabId }),
        },
      });
    },
    [push, sendEventToAnalytics, tabs]
  );

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchBonuses();
  }, [fetchBonuses, isAuthenticated]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentTabIndex]);

  useEffect(() => {
    if (!isAuthenticated && !pendingUser) {
      setCurrentTabIndex(0);
    }
  }, [isAuthenticated, pendingUser]);

  return (
    <>
      {currentCancelBonusData && (
        <CancelBonusModal {...currentCancelBonusData} />
      )}
      <ScContainer>
        <ScPromotionsPage>
          {isAuthenticated && (
            <TabNavigation
              selectedItem={currentTabIndex}
              setSelectedItem={handleSelectTab}
              navItems={tabs.map(({ title }) => title)}
              withoutShadow
              {...tabNavigationProps}
            />
          )}
          {currentTabComponent}
        </ScPromotionsPage>
      </ScContainer>
    </>
  );
};
